import { z } from 'zod';

import { captionStyleRefSchema } from '../caption-styles';
import { defineNode, emits, TRIGGER_INPUT } from '../define';

const SOURCE_OUT = [{ id: 'out', type: 'source' }] as const;

export const competitorWinners = defineNode({
  type: 'starter.competitorWinners',
  version: 1,
  category: 'starter',
  label: 'Competitor winners',
  description:
    'Reels from tracked competitors beating their own average — e.g. ≥ 2× in 7 days.',
  icon: 'Binoculars',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: SOURCE_OUT,
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    /**
     * Competitors (by id) to watch; empty means every tracked competitor.
     * Flows saved before competitors could lack a site hold sites here, which
     * still match.
     */
    competitors: z.array(z.string().min(1)).max(10).default([]).meta({
      title: 'Competitors',
      description: 'Leave empty to watch them all',
      widget: 'competitors',
    }),
    minLift: z.number().min(1).max(20).default(2).meta({
      title: 'Beats their average by',
      widget: 'multiplier',
    }),
    windowDays: z
      .int()
      .min(1)
      .max(90)
      .default(7)
      .meta({ title: 'Posted in the last', widget: 'days' }),
    maxItems: z.int().min(1).max(50).default(12).meta({ title: 'At most' }),
  }),
  cardinality: (config) => emits(config.maxItems),
});

export const myTopPosts = defineNode({
  type: 'starter.myTopPosts',
  version: 1,
  category: 'starter',
  label: 'My top posts',
  description: 'Your best performers by views, watch time or saves.',
  icon: 'TrendUp',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: SOURCE_OUT,
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    metric: z.enum(['views', 'watchTime', 'saves']).default('views').meta({
      title: 'Ranked by',
      widget: 'segmented',
    }),
    windowDays: z
      .int()
      .min(1)
      .max(365)
      .default(30)
      .meta({ title: 'Posted in the last', widget: 'days' }),
    limit: z.int().min(1).max(20).default(1).meta({ title: 'How many' }),
  }),
  cardinality: (config) => emits(config.limit),
});

export const referencePost = defineNode({
  type: 'starter.referencePost',
  version: 1,
  category: 'starter',
  label: 'Reference post',
  description: 'Paste a TikTok, Instagram or YouTube URL, or upload a file.',
  icon: 'Link',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: SOURCE_OUT,
  mode: 'map',
  executor: 'control',
  config: z
    .object({
      url: z
        .url({ protocol: /^https?$/, error: 'Paste a full link, starting with https://' })
        .optional()
        .meta({ title: 'Post URL' }),
      mediaAssetId: z
        .uuid()
        .optional()
        .meta({ title: 'Or an upload', widget: 'mediaAsset' }),
    })
    .refine((config) => config.url !== undefined || config.mediaAssetId !== undefined, {
      error: 'Paste a post URL or pick an upload',
    }),
  cardinality: () => emits(1),
});

export const library = defineNode({
  type: 'starter.library',
  version: 1,
  category: 'starter',
  label: 'Library',
  description: 'Clips and reels already in your media library.',
  icon: 'Folder',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [{ id: 'out', type: 'clip' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    collectionId: z.uuid().optional().meta({ title: 'Collection', widget: 'collection' }),
    assetIds: z
      .array(z.uuid())
      .max(50)
      .default([])
      .meta({ title: 'Or pick items', widget: 'mediaAssets' }),
    limit: z.int().min(1).max(50).default(10).meta({ title: 'At most' }),
  }),
  cardinality: (config) => emits(config.assetIds.length || config.limit),
});

export const collectionSources = defineNode({
  type: 'starter.collectionSources',
  version: 1,
  category: 'starter',
  label: 'From a collection',
  description:
    'What a collection holds: its posts to remake (a smart one can include competitors’ posts not saved yet), and its images.',
  icon: 'SlidersHorizontal',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [
    { id: 'out', label: 'Posts', type: 'source' },
    { id: 'images', label: 'Images', type: 'image' },
  ],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    collectionId: z
      .uuid({ error: 'Pick a collection' })
      .meta({ title: 'Collection', widget: 'collection' }),
    limit: z.int().min(1).max(50).default(10).meta({ title: 'At most' }),
  }),
  cardinality: (config) => ({ out: config.limit, images: config.limit }),
});

export const collectionImages = defineNode({
  type: 'starter.collectionImages',
  version: 1,
  category: 'starter',
  label: 'Images',
  description:
    'One image from your library, or a collection’s images - each with the persona it shows.',
  icon: 'Image',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [{ id: 'out', type: 'image' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z
    .object({
      from: z.enum(['oneImage', 'collection']).default('collection').meta({
        title: 'From',
        widget: 'segmented',
      }),
      mediaAssetId: z
        .uuid()
        .optional()
        .meta({ title: 'Image', widget: 'image', when: { from: ['oneImage'] } }),
      collectionId: z
        .uuid()
        .optional()
        .meta({
          title: 'Collection',
          widget: 'collection',
          when: { from: ['collection'] },
        }),
      limit: z
        .int()
        .min(1)
        .max(100)
        .default(20)
        .meta({ title: 'At most', when: { from: ['collection'] } }),
    })
    .refine((config) => config.from !== 'oneImage' || config.mediaAssetId !== undefined, {
      error: 'Pick an image',
      path: ['mediaAssetId'],
    })
    .refine(
      (config) => config.from !== 'collection' || config.collectionId !== undefined,
      {
        error: 'Pick a collection',
        path: ['collectionId'],
      },
    ),
  cardinality: (config) => emits(config.from === 'oneImage' ? 1 : config.limit),
});

export const reelScenes = defineNode({
  type: 'starter.reelScenes',
  version: 1,
  category: 'starter',
  label: 'Reel scenes',
  description: 'Scenes of a reel in your library - each as its frame, to remake.',
  icon: 'FilmStrip',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [{ id: 'out', type: 'image' }],
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    mediaAssetId: z
      .uuid({ error: 'Pick a reel' })
      .meta({ title: 'Reel', widget: 'reel' }),
    scenes: z.array(z.int().min(1)).max(30).default([]).meta({
      title: 'Scenes',
      description: 'None ticked: every scene',
      widget: 'scenes',
    }),
  }),
  cardinality: (config) => emits(config.scenes.length || 10),
});

/** One slide position in Carousel from slots: where its image and its text come from. */
export const slideSlotSchema = z.object({
  label: z.string().trim().min(1).max(40),
  imagesCollectionId: z.uuid({ error: 'Pick the collection its images come from' }),
  captionsCollectionId: z.uuid().optional(),
  position: z.enum(['top', 'center', 'bottom']).default('bottom'),
  /** Its own caption style; unset uses the carousel's. */
  style: captionStyleRefSchema.optional(),
});
export type SlideSlot = z.output<typeof slideSlotSchema>;

export const slotCarousel = defineNode({
  type: 'starter.slotCarousel',
  version: 1,
  category: 'starter',
  label: 'Carousel from slots',
  description:
    'Carousels made from your slide pools: each slide picks an image and a caption from its collections.',
  icon: 'Cards',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [{ id: 'out', type: 'carousel' }],
  mode: 'map',
  // Composites images: drawn by the render worker but not charged as a render.
  executor: 'render',
  // A pool can run out (unique images) before the count is reached.
  bounded: true,
  config: z.object({
    slots: z
      .array(slideSlotSchema)
      .min(1, { error: 'Add a slide' })
      .max(10)
      .default([])
      .meta({
        title: 'Slides',
        description: 'In order. Each carousel takes one image and one caption per slide.',
        widget: 'slots',
      }),
    count: z.int().min(1).max(50).default(10).meta({ title: 'Carousels per run' }),
    // Unset: the workspace's default style, then White band.
    style: captionStyleRefSchema.optional().meta({
      title: 'Caption style',
      description: 'Used on every slide unless a slide picks its own.',
      widget: 'captionStyle',
    }),
    uniqueImages: z.boolean().default(false).meta({
      title: 'Never reuse an image',
      description: 'Across carousels that weren’t rejected. Stops when a pool runs out.',
    }),
  }),
  cardinality: (config) => emits(config.count),
});

export const brief = defineNode({
  type: 'starter.brief',
  version: 1,
  category: 'starter',
  label: 'Brief',
  description: 'Start from a written idea — no reference needed.',
  icon: 'FileText',
  status: 'ga',
  inputs: [TRIGGER_INPUT],
  outputs: [{ id: 'out', type: 'script' }],
  mode: 'map',
  executor: 'control',
  config: z.object({
    brief: z
      .string()
      .trim()
      .min(1, 'Write the idea this flow should turn into posts')
      .max(2000)
      .default('')
      .meta({ title: 'Brief', widget: 'textarea' }),
  }),
  cardinality: () => emits(1),
});

export const schedule = defineNode({
  type: 'starter.schedule',
  version: 1,
  category: 'starter',
  label: 'Schedule',
  description: 'Run this flow on a timetable, e.g. every Monday 09:00.',
  icon: 'Clock',
  status: 'ga',
  inputs: [],
  outputs: [{ id: 'out', type: 'trigger' }],
  mode: 'map',
  executor: 'control',
  config: z.object({
    cron: z
      .string()
      .trim()
      .regex(/^(\S+\s+){4}\S+$/, 'Use five cron fields, e.g. 0 9 * * 1')
      .default('0 9 * * 1')
      .meta({ title: 'When', widget: 'schedule' }),
    /** Empty means the workspace's time zone. */
    timezone: z.string().max(64).optional().meta({ title: 'Time zone' }),
  }),
  cardinality: () => ({}),
});

export const webhook = defineNode({
  type: 'starter.webhook',
  version: 1,
  category: 'starter',
  label: 'Webhook',
  description: 'Start when another app sends a video or a brief.',
  icon: 'WebhooksLogo',
  status: 'ga',
  inputs: [],
  outputs: SOURCE_OUT,
  mode: 'map',
  executor: 'control',
  config: z.object({}),
  cardinality: () => emits(1),
});

export const trendingFormats = defineNode({
  type: 'starter.trendingFormats',
  version: 1,
  category: 'starter',
  label: 'Trending formats',
  description: 'Rising sounds and formats in your niche.',
  icon: 'Fire',
  status: 'soon',
  inputs: [TRIGGER_INPUT],
  outputs: SOURCE_OUT,
  mode: 'map',
  executor: 'control',
  bounded: true,
  config: z.object({
    limit: z.int().min(1).max(20).default(5).meta({ title: 'How many' }),
  }),
  cardinality: (config) => emits(config.limit),
});

export const STARTERS = [
  competitorWinners,
  myTopPosts,
  referencePost,
  library,
  collectionSources,
  collectionImages,
  reelScenes,
  slotCarousel,
  brief,
  schedule,
  webhook,
  trendingFormats,
];
